/**
 * ConversationsPage — chronological list of the founder's voice sessions.
 *
 * Rows are read via supabase-js (PostgREST + RLS). Real sessions land
 * as soon as Phase 1C persistence is wired (which it now is). Empty
 * state remains honest: nothing shown = nothing recorded.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessagesSquare, Search, Loader2, ChevronRight } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { supabase } from "@/lib/supabaseClient";
import { useActiveProject } from "@/context/ActiveProjectContext";

function StatePill({ status }) {
    const tone =
        status === "open" ? "text-qoryn-cyan bg-qoryn-cyan/10" :
        status === "ended" ? "text-qoryn-textDim bg-white/5" :
        status === "error" ? "text-red-300 bg-red-500/10" :
        "text-qoryn-textMuted bg-white/5";
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono-ui text-[9px] tracking-widest uppercase ${tone}`}>
            {status}
        </span>
    );
}

export default function ConversationsPage() {
    const { projects } = useActiveProject();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [projectFilter, setProjectFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        supabase
            .from("voice_sessions")
            .select("id, title, project_id, status, started_at, duration_seconds")
            .order("started_at", { ascending: false })
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) setError(error.message);
                else setRows(data ?? []);
            });
        return () => { cancelled = true; };
    }, []);

    const projectName = useMemo(() => {
        const m = new Map(projects.map((p) => [p.id, p.name]));
        return (pid) => (pid && m.get(pid)) || null;
    }, [projects]);

    const filtered = useMemo(() => {
        if (!rows) return [];
        return rows
            .filter((r) => projectFilter === "all" || r.project_id === projectFilter)
            .filter((r) => !query.trim() || (r.title || "").toLowerCase().includes(query.toLowerCase()));
    }, [rows, projectFilter, query]);

    return (
        <div data-testid="conversations-page" className="space-y-6">
            <div className="flex items-baseline gap-4 flex-wrap">
                <h1 className="font-display text-[28px] font-light tracking-tight text-qoryn-text">Conversations</h1>
                <p className="font-sans text-[13px] text-qoryn-textDim">
                    Every voice session you have with Qoryn, chronological.
                </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="qoryn-panel rounded-full flex items-center gap-2 px-3 h-9 flex-1 min-w-[220px] max-w-md">
                    <Search className="h-3.5 w-3.5 text-qoryn-textMuted" strokeWidth={1.75} />
                    <input
                        data-testid="conversations-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search conversations by title"
                        className="flex-1 bg-transparent border-0 outline-none font-sans text-[13px] text-qoryn-text placeholder:text-qoryn-textMuted"
                    />
                </div>
                <select
                    data-testid="conversations-project-filter"
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    className="h-9 px-3 rounded-full qoryn-panel bg-transparent font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-text qoryn-focus"
                >
                    <option value="all">All projects</option>
                    {projects.filter((p) => p.status !== "archived").map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>

            {rows === null && !error && (
                <div className="py-10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-qoryn-cyan" /></div>
            )}
            {error && (
                <EmptyState icon={MessagesSquare} title="Couldn't load conversations." body={error} />
            )}
            {rows !== null && !error && filtered.length === 0 && (
                <EmptyState
                    icon={MessagesSquare}
                    title="No conversations yet."
                    body="Start a voice session on the Founder Console — the transcript, extraction, and audit chain will land here."
                />
            )}
            {filtered.length > 0 && (
                <ul data-testid="conversations-list" className="space-y-2">
                    {filtered.map((s) => (
                        <li key={s.id}>
                            <Link
                                to={`/conversations/${s.id}`}
                                data-testid={`session-row-${s.id}`}
                                className="qoryn-panel rounded-2xl px-5 py-4 flex items-center gap-4 hover:bg-white/[0.03] transition-colors"
                            >
                                <div className="flex-1 min-w-0 space-y-1">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span className="font-sans text-[14px] text-qoryn-text truncate">
                                            {s.title || "Untitled session"}
                                        </span>
                                        <StatePill status={s.status} />
                                    </div>
                                    <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted flex items-center gap-2 flex-wrap">
                                        <span>{new Date(s.started_at).toLocaleString()}</span>
                                        {s.duration_seconds != null && <span>· {s.duration_seconds}s</span>}
                                        {projectName(s.project_id) && <span>· {projectName(s.project_id)}</span>}
                                    </div>
                                </div>
                                <ChevronRight className="h-4 w-4 text-qoryn-textMuted flex-shrink-0" strokeWidth={1.75} />
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
